/**
 * Normaliza URLs de vídeo para um formato que o react-player reconheça.
 *
 * O react-player identifica o provider por regex. Se a URL não casar com
 * nenhum padrão conhecido ele cai no FilePlayer e renderiza um <video src="...">,
 * que falha em silêncio (tela em branco) para links do YouTube.
 *
 * URLs que não são do YouTube (ex: bucket da AWS) são devolvidas intactas.
 */

const YOUTUBE_ID = '([\\w-]{11})';

const YOUTUBE_PATTERNS = [
  // https://youtu.be/ID
  new RegExp(`youtu\\.be/${YOUTUBE_ID}`),
  // https://www.youtube.com/watch?v=ID  |  ...?si=x&v=ID
  new RegExp(
    `youtube(?:-nocookie|education)?\\.com/watch\\?(?:.*&)?v=${YOUTUBE_ID}`,
  ),
  // /embed/ID  |  /v/ID  |  /shorts/ID  |  /live/ID
  new RegExp(
    `youtube(?:-nocookie|education)?\\.com/(?:embed|v|shorts|live)/${YOUTUBE_ID}`,
  ),
];

/** Extrai o ID de 11 caracteres de qualquer formato conhecido do YouTube. */
export const extractYouTubeId = (rawUrl: string): string | null => {
  if (!rawUrl) return null;

  // Aceita um <iframe src="..."> colado inteiro no campo do backend.
  const iframeSrc = rawUrl.match(/<iframe[^>]+src=["']([^"']+)["']/i);
  const url = iframeSrc ? iframeSrc[1] : rawUrl.trim();

  const matched = YOUTUBE_PATTERNS.map(pattern => url.match(pattern)).find(
    Boolean,
  );

  return matched ? matched[1] : null;
};

export const isYouTubeUrl = (rawUrl?: string): boolean =>
  !!rawUrl && extractYouTubeId(rawUrl) !== null;

/**
 * Preserva o tempo inicial (?t=90, ?start=90, #t=1m30s) quando presente —
 * o react-player lê esses parâmetros da URL canônica.
 */
const extractStartParam = (url: string): string => {
  const match = url.match(/[?&#](?:t|start)=([\dhms]+)/i);
  return match ? `&t=${match[1]}` : '';
};

export const normalizeVideoUrl = (rawUrl?: string): string => {
  if (!rawUrl) return '';

  const url = rawUrl.trim();
  const youtubeId = extractYouTubeId(url);

  if (!youtubeId) return url;

  return `https://www.youtube.com/watch?v=${youtubeId}${extractStartParam(url)}`;
};

export default normalizeVideoUrl;
